/**
 * The Automations tab and one automation, as the redesign confirmed them (c-build/v-autos.js): every automation
 * as a card with its line and its rooms, a filter per room and New automation; and one automation's page with
 * its flow on a canvas, edited after Edit steps, beside who is in it, how it is doing, what needs attention and
 * the rooms it runs on.
 */
import { type ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { attentionWords, openedShare } from "../../shared/automation-words";
import type {
  AutomationDetail,
  AutomationListResponse,
  AutomationPeople,
  AutomationStatus,
  AutomationSummary,
  WebinarSummary,
} from "../../shared/types";
import { useMaybeUser } from "../auth/AuthContext";
import { SeasonAutomation } from "../automations/SeasonAutomation";
import { SeasonAutomations } from "../automations/SeasonAutomations";
import { unfinished } from "../automations/flow-model";
import { STARTS, type Start } from "../automations/starts";
import { useAutomation } from "../automations/useAutomation";
import { createFromStart, useAutomations } from "../automations/useAutomations";
import { errorText, firstRoomOnly, fmt, howRows, people, waitingRoom, whereWords } from "../automations/words";
import { type Editor, FlowEditor } from "../components/FlowEditor";
import { FlowLine, summaryLine } from "../components/FlowLine";
import { InviteSheet, inviteTargetOf } from "../components/InviteSheet";
import { useLayout } from "../components/season/layout";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Menu } from "../components/ui/Menu";
import { PageHeader } from "../components/ui/PageHeader";
import { Chip, Pill } from "../components/ui/Pill";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { shareLink, shareToast } from "../lib/shareLink";
import { useSendingStatus } from "../sending/SendingContext";
import { heldReason } from "../sending/sendingProtection";
import "../styles/automations.css";

export function AutomationsView() {
  const id = useParams<{ id: string }>().id;
  const layout = useLayout();
  if (layout === "computer") return id ? <AutomationPage key={id} id={id} /> : <AutomationsList />;
  if (layout === "side") return <SeasonAutomations layout="side" openId={id} />;
  return id ? <SeasonAutomation key={id} id={id} layout={layout} /> : <SeasonAutomations layout={layout} />;
}

// ── what an automation is doing right now ───────────────────────────────
function StatusPill({ status, waiting }: { status: AutomationStatus; waiting: boolean }) {
  if (status === "draft") return <Pill tone="warn">Draft</Pill>;
  if (waiting) return <Pill tone="warn">Starts when published</Pill>;
  return status === "on" ? (
    <Pill tone="good" dot>
      On
    </Pill>
  ) : (
    <Pill dot>Off</Pill>
  );
}

/**
 * "1,540 joined · 71% opened · 38 signed up", or "38 handed off", from its own runs. Signed up is the design's word
 * for the people who became customers, the same count as Became customers on its page.
 */
function Numbers({ a }: { a: AutomationSummary }) {
  const e = a.enroll;
  if (a.status === "draft" || !e) return <div className="anums" />;
  const opened = openedShare(e);
  if (e.handed && !a.graph.nodes.some((n) => n.type === "email")) {
    return (
      <div className="anums">
        <span>
          <b>{fmt(e.handed)}</b> handed off
        </span>
      </div>
    );
  }
  return (
    <div className="anums">
      <span>
        <b>{fmt(e.entered)}</b> joined
      </span>
      {opened === null ? null : (
        <span>
          <b>{opened}%</b> opened
        </span>
      )}
      <span>
        <b>{fmt(e.signed)}</b> signed up
      </span>
    </div>
  );
}

// ── the list ────────────────────────────────────────────────────────────
function AutomationsList() {
  const navigate = useNavigate();
  const sending = useSendingStatus();
  const { data, failed, load, switchOn } = useAutomations();
  const [filter, setFilter] = useState("all");
  const [making, setMaking] = useState(false);

  const header = (sub?: ReactNode) => (
    <PageHeader
      title="Automations"
      sub={sub}
      actions={
        <Button variant="p" icon="plus" onClick={() => setMaking(true)}>
          New automation
        </Button>
      }
    />
  );
  const sheet = <NewAutomation open={making} onClose={() => setMaking(false)} />;

  if (failed) {
    return (
      <div className="page">
        {header()}
        <EmptyState
          icon="zap"
          title="Your automations did not load"
          actions={
            <Button icon="refresh" onClick={load}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page" aria-busy="true">
        {header()}
      </div>
    );
  }
  if (!data.automations.length) {
    return (
      <div className="page">
        <PageHeader title="Automations" />
        <section className="card">
          <EmptyState
            icon="zap"
            title="Follow-ups come with each room"
            actions={
              <>
                <Button variant="p" icon="upload" onClick={() => navigate("/rooms/new?from=recording")}>
                  From a recording
                </Button>
                <Button icon="live" onClick={() => navigate("/rooms/new?from=live")}>
                  A live room
                </Button>
              </>
            }
          />
        </section>
      </div>
    );
  }

  const fresh = firstRoomOnly(data);
  if (fresh) return <ReadyState data={data} roomId={fresh} />;

  const list = data.automations;
  const rooms = new Map<string, string>();
  for (const a of list) for (const r of a.rooms) rooms.set(r.webinar_id, r.title);
  const shown = filter === "all" ? list : list.filter((a) => a.rooms.some((r) => r.webinar_id === filter));
  const held = heldReason(sending);

  return (
    <div className="page">
      {header(
        <>
          <b className="ink2">{people(data.people_now)}</b> in them right now
        </>,
      )}
      {held && held !== "paused" ? (
        <div className="strip">
          <div className="ic">
            <Icon name="mail" />
          </div>
          <div className="sp">
            <b>These emails won’t go out yet.</b>
          </div>
          <Button size="s" variant="p" onClick={() => navigate("/setup/email")}>
            Set up sending
          </Button>
        </div>
      ) : null}
      <div className="row g8 wrap" style={{ marginBottom: 18 }}>
        <Chip on={filter === "all"} count={list.length} onClick={() => setFilter("all")}>
          All
        </Chip>
        {[...rooms].map(([id, title]) => (
          <Chip key={id} on={filter === id} onClick={() => setFilter(id)}>
            {title}
          </Chip>
        ))}
      </div>
      <div className="col" style={{ gap: 14 }}>
        {shown.map((a) => (
          <AutomationCard key={a.id} a={a} onOpen={() => navigate(`/automations/${a.id}`)} onSwitch={(on) => switchOn(a, on)} />
        ))}
      </div>
      {sheet}
    </div>
  );
}

/** One automation on the list: its name, numbers and switch, its line, and the rooms it runs on. */
function AutomationCard({ a, onOpen, onSwitch }: { a: AutomationSummary; onOpen: () => void; onSwitch: (on: boolean) => void }) {
  const on = a.status === "on";
  const waiting = !!waitingRoom(a.status, a.rooms);
  return (
    <article
      className={on || a.status === "draft" ? "card acard" : "card acard off"}
      role="link"
      tabIndex={0}
      aria-label={a.name}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) onOpen();
      }}
    >
      <div className="acard-top">
        <h3>{a.name}</h3>
        {a.attention ? (
          <Pill tone="warn" dot>
            {a.attention}
          </Pill>
        ) : null}
        <span className="sp" />
        <Numbers a={a} />
        {a.status === "draft" ? (
          <Pill tone="warn">Draft</Pill>
        ) : (
          <>
            {waiting ? <Pill tone="warn">Starts when published</Pill> : null}
            {/* Why: the switch sits on a card that opens; switching must not open it. */}
            <span className="row" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <Toggle on={on} onChange={onSwitch} label={`${on ? "Turn off" : "Turn on"} ${a.name}`} />
            </span>
          </>
        )}
      </div>
      <div className="acard-l">
        <FlowLine {...summaryLine(a)} />
      </div>
      <div className="arms">
        {a.rooms.length ? (
          a.rooms.map((r) => (
            <span key={r.webinar_id} className={r.enabled ? "arm" : "arm off"}>
              {r.poster_url ? (
                <img src={r.poster_url} alt="" />
              ) : (
                <span className="arm-d">
                  <Icon name="pencil" />
                </span>
              )}
              {r.title}
            </span>
          ))
        ) : (
          <span className="arm none">Not on a room yet</span>
        )}
      </div>
    </article>
  );
}

function ReadyState({ data, roomId }: { data: AutomationListResponse; roomId: string }) {
  const toast = useToast();
  const [room, setRoom] = useState<WebinarSummary | null>(null);
  const [inviting, setInviting] = useState(false);
  const title = data.automations.flatMap((a) => a.rooms).find((r) => r.webinar_id === roomId)?.title ?? "";
  useEffect(() => {
    api.webinars
      .list()
      .then((l) => setRoom(l.webinars.find((w) => w.id === roomId) ?? null))
      .catch(() => setRoom(null));
  }, [roomId]);
  const copy = (w: WebinarSummary) =>
    void shareLink(w.register_url, w.title).then((outcome) => {
      const said = shareToast(outcome);
      if (said) toast(said);
    });
  return (
    <div className="page">
      <PageHeader title="Automations" />
      <section className="card">
        <EmptyState
          icon="zap"
          title={`Follow-ups for ${title} are ready`}
          text="0 people in them so far"
          actions={
            room ? (
              <>
                <Button variant="p" icon="link" onClick={() => copy(room)}>
                  Copy the sign-up link
                </Button>
                <Button icon="userplus" onClick={() => setInviting(true)}>
                  Invite people
                </Button>
              </>
            ) : null
          }
        />
      </section>
      {inviting && room ? (
        <InviteSheet
          webinar={inviteTargetOf(room)}
          onClose={() => setInviting(false)}
          onSent={(send) => toast(`Sending ${fmt(send.total)} ${send.total === 1 ? "invite" : "invites"}`)}
          onToast={(m) => toast(m)}
        />
      ) : null}
    </div>
  );
}

/** New automation: a ready-made start, or nothing, made at once and opened to edit. */
function NewAutomation({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [pick, setPick] = useState<Start["key"]>(STARTS[0].key);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    const start = STARTS.find((s) => s.key === pick) ?? STARTS[0];
    setBusy(true);
    try {
      const made = await createFromStart(start);
      onClose();
      navigate(`/automations/${made.id}`, { state: { edit: true } });
    } catch (err) {
      toast(errorText(err, "The automation was not made"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New automation"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" after="right" onClick={() => void make()} disabled={busy}>
            Continue
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 8 }} role="radiogroup" aria-label="Start from">
        {STARTS.map((s) => (
          <div
            key={s.key}
            className={pick === s.key ? "radio on" : "radio"}
            role="radio"
            aria-checked={pick === s.key}
            tabIndex={0}
            onClick={() => setPick(s.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setPick(s.key);
              }
            }}
          >
            <span className="rd" />
            <div>
              <div className="b7" dir="auto">{s.title}</div>
              {s.line ? <div className="small muted" dir="auto">{s.line}</div> : null}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// ── one automation ──────────────────────────────────────────────────────
function AutomationPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const user = useMaybeUser();
  const A = useAutomation(id);
  const { detail, editor, setEditor, who, missing } = A;
  const [edit, setEdit] = useState(() => !!(location.state as { edit?: boolean } | null)?.edit);
  const [saving, setSaving] = useState(false);

  if (missing) {
    return (
      <div className="page">
        <PageHeader title="Automations" back={{ to: "/automations", label: "Automations" }} />
        <EmptyState
          icon="zap"
          title={missing}
          actions={<Button onClick={() => navigate("/automations")}>All automations</Button>}
        />
      </div>
    );
  }
  if (!detail || !editor) {
    return (
      <div className="page" aria-busy="true">
        <PageHeader title="" back={{ to: "/automations", label: "Automations" }} />
      </div>
    );
  }

  const d = detail;
  const status = d.status;
  const attached = d.webinars.filter((w) => w.attached).map((w) => ({ ...w, enabled: w.enabled === 1 }));
  const waiting = waitingRoom(status, attached);
  const inNow = who ? Object.values(who.at).reduce((n, l) => n + l.length, 0) : 0;
  const onRooms = attached.filter((r) => r.enabled);
  const sessions = onRooms.length ? onRooms.some((r) => r.has_sessions) : undefined;

  const setStatus = (next: AutomationStatus) => A.setStatus(d, next);
  const setRoom = (webinarId: string, on: boolean) => A.setRoom(d, webinarId, on);

  const done = async () => {
    // Why: the server refuses a flow with an unfinished email; say what is missing instead of its error.
    const unready = unfinished(editor.trigger, editor.steps);
    if (unready) {
      toast({ text: unready.words, icon: "info" });
      return;
    }
    setSaving(true);
    try {
      // Why: Finish it ends a draft, which is then switched on by hand; saving never starts it sending.
      const nextStatus: AutomationStatus = status === "draft" && editor.steps.length ? "off" : status;
      await A.saveFlow(editor, nextStatus);
      setEdit(false);
      toast("Steps saved.");
    } catch (err) {
      toast(errorText(err, "The steps did not save"));
    } finally {
      setSaving(false);
    }
  };

  const rename = (name: string) => A.rename(d, editor, name);
  const mail = {
    hostName: user ? `${user.first_name} ${user.last_name}`.trim() : "",
    workspaceName: user?.workspace_name ?? "",
    offer: null,
  };

  return (
    <div className="page">
      <PageHeader
        back={{ to: "/automations", label: "Automations" }}
        title={
          <span className="row g12">
            <Name value={editor.name} onRename={rename} />
            <StatusPill status={status} waiting={!!waiting} />
          </span>
        }
        actions={
          <>
            {status === "draft" ? (
              edit ? null : (
                <Button variant="p" onClick={() => setEdit(true)}>
                  Finish it
                </Button>
              )
            ) : (
              <div className="row g8 card flat" style={{ padding: "8px 8px 8px 14px", borderRadius: 14 }}>
                <span className="b7">{status === "on" ? "On" : "Off"}</span>
                <Toggle large on={status === "on"} onChange={(on) => setStatus(on ? "on" : "off")} label={status === "on" ? "Turn off" : "Turn on"} />
              </div>
            )}
            {edit ? (
              <>
                <DeleteAutomation d={d} onDelete={() => A.remove(d)} />
                <Button variant="p" icon="check" onClick={() => void done()} disabled={saving}>
                  Done
                </Button>
              </>
            ) : status === "draft" ? null : (
              <Button icon="pencil" onClick={() => setEdit(true)}>
                Edit steps
              </Button>
            )}
          </>
        }
      />
      {waiting ? (
        <div className="offbanner">
          <Icon name="clock" />
          <div className="sp">
            <b>Starts when <bdi>{waiting.title}</bdi> is published.</b>
          </div>
          <Button size="s" onClick={() => navigate(`/rooms/${waiting.webinar_id}`)}>
            Open the room
          </Button>
        </div>
      ) : null}
      {status === "off" ? (
        <div className="offbanner">
          <Icon name="pause" />
          <div className="sp">
            <b>Paused.</b>
            {inNow ? ` ${people(inNow)} wait where they are.` : ""}
          </div>
          <Button size="s" onClick={() => setStatus("on")}>
            Turn it back on
          </Button>
        </div>
      ) : null}
      <div className="adet">
        <div className="fla-wrap">
          <div className={edit ? "canvas fla-edit" : "canvas"}>
            <FlowEditor
              editor={editor}
              setEditor={(next) => setEditor((prev) => (prev ? (typeof next === "function" ? next(prev) : next) : prev))}
              onToast={(m) => toast(m)}
              mail={mail}
              people={who}
              automationId={id}
              editing={edit}
              paused={status === "off"}
              {...(sessions === undefined ? {} : { sessions })}
              whenSub={status === "draft" ? "Not running yet" : d.enroll ? `${fmt(d.enroll.entered)} joined so far` : ""}
            />
          </div>
        </div>
        <div className="col" style={{ gap: 20 }}>
          <InItNow who={who} editor={editor} />
          <HowItsDoing d={d} />
          <NeedsAttention d={d} onSettle={(how) => A.settleRuns(d, how)} />
          <RunsOn d={d} onRoom={setRoom} />
        </div>
      </div>
    </div>
  );
}

/** The name, renamed in place by clicking it. */
function Name({ value, onRename }: { value: string; onRename: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const start = () => {
    setText(value);
    setEditing(true);
  };
  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        title="Click to rename"
        onClick={start}
        onKeyDown={(e) => {
          if (e.key === "Enter") start();
        }}
      >
        {value}
      </span>
    );
  }
  return (
    <input dir="auto"
      className="aname-edit"
      aria-label="Automation name"
      value={text}
      autoFocus
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        setEditing(false);
        onRename(text);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setText(value);
          setEditing(false);
        }
      }}
    />
  );
}

function DeleteAutomation({ d, onDelete }: { d: AutomationDetail; onDelete: () => Promise<unknown> }) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <Button variant="q" icon="trash" onClick={() => setAsking(true)}>
        Delete
      </Button>
      <Sheet
        open={asking}
        onClose={() => setAsking(false)}
        title="Delete this automation?"
        sub="Anyone in it stops where they are."
        footer={
          <>
            <Button onClick={() => setAsking(false)}>Cancel</Button>
            <Button variant="p" onClick={() => void onDelete()}>
              Delete
            </Button>
          </>
        }
      >
        <p className="small muted">{d.name}</p>
      </Sheet>
    </>
  );
}

function InItNow({ who, editor }: { who: AutomationPeople | null; editor: Editor }) {
  const navigate = useNavigate();
  const everyone = who ? Object.entries(who.at).flatMap(([node, list]) => list.map((p) => ({ p, node }))) : [];
  const first = everyone.slice(0, 4);
  const rest = everyone.slice(4);
  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h2>In it right now</h2>
          <div className="sub">{everyone.length ? people(everyone.length) : "No one at the moment"}</div>
        </div>
      </div>
      {everyone.length ? (
        <div className="card-b col" style={{ gap: 10, paddingTop: 12 }}>
          {first.map(({ p, node }) => (
            <div
              key={p.run_id}
              className="who"
              role="link"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/people/${p.registrant_id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/people/${p.registrant_id}`);
              }}
            >
              <Face name={p.name || p.email} photo={p.avatar_url} size={36} />
              <div>
                <div className="nm">{p.name || p.email}</div>
                <div className="ds">{whereWords(p, node, editor)}</div>
              </div>
            </div>
          ))}
          {rest.length ? (
            <div className="nmore">
              <FaceStack people={rest.slice(0, 4).map(({ p }) => ({ name: p.name || p.email, photo: p.avatar_url }))} size={24} />
              <span>and {fmt(rest.length)} more</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function HowItsDoing({ d }: { d: AutomationDetail }) {
  const bars = howRows(d);
  if (!bars) return null;
  return (
    <section className="card">
      <div className="card-h">
        <h2>How it’s doing</h2>
      </div>
      <div className="card-b col" style={{ gap: 12, paddingTop: 12 }}>
        {bars.map(([label, n, w]) => (
          <div key={label}>
            <div className="row small">
              <span className="sp ink2 b6">{label}</span>
              <b>{n}</b>
            </div>
            <div className="bar mt6" style={{ height: 8, borderRadius: 4 }}>
              <i style={{ width: `${Math.min(100, w)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function NeedsAttention({ d, onSettle }: { d: AutomationDetail; onSettle: (how: "retry" | "stop") => Promise<void> }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const runs = d.failed_runs;
  const words = attentionWords(runs);
  const reasons = [...new Set(runs.map((r) => r.error.trim()).filter(Boolean))];
  const act = async (how: "retry" | "stop") => {
    setBusy(true);
    try {
      await onSettle(how);
    } catch (err) {
      toast(errorText(err, "That did not work just now"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <div className="card-h">
        <h2>Needs attention</h2>
      </div>
      <div className="card-b">
        {words ? (
          <div className="note warn">
            <Icon name="info" />
            <div>
              <b>{words}.</b>
              {reasons.length === 1 ? ` ${reasons[0].replace(/\.$/, "")}.` : ""}
              <div className="row g8 mt8">
                <Button size="s" onClick={() => void act("retry")} disabled={busy}>
                  Try again
                </Button>
                <Button size="s" variant="q" onClick={() => void act("stop")} disabled={busy}>
                  Stop them
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="note good">
            <Icon name="check" />
            <div>All clear.</div>
          </div>
        )}
      </div>
    </section>
  );
}

function RunsOn({ d, onRoom }: { d: AutomationDetail; onRoom: (webinarId: string, on: boolean) => void }) {
  const navigate = useNavigate();
  const attached = d.webinars.filter((w) => w.attached);
  const others = d.webinars.filter((w) => !w.attached);
  return (
    <section className="card">
      <div className="card-h">
        <h2>Runs on</h2>
        {others.length ? (
          <Menu
            label="Add a room"
            icon="plus"
            items={others.map((w) => ({ label: w.title, onSelect: () => onRoom(w.webinar_id, true) }))}
          />
        ) : null}
      </div>
      <div className="card-b col" style={{ gap: 10, paddingTop: 12 }}>
        {attached.length ? (
          attached.map((w) => (
            <div key={w.webinar_id} className="row g12">
              <span
                className="row g12 sp"
                role="link"
                tabIndex={0}
                style={{ cursor: "pointer", minWidth: 0 }}
                onClick={() => navigate(`/rooms/${w.webinar_id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate(`/rooms/${w.webinar_id}`);
                }}
              >
                {w.poster_url ? (
                  <img className="arun-pic" src={w.poster_url} alt="" />
                ) : (
                  <span className="arun-draft">
                    <Icon name="pencil" />
                  </span>
                )}
                <span className="sp b6 small" dir="auto">{w.title}</span>
              </span>
              {w.enabled === 1 ? null : <Pill>Off here</Pill>}
              <Toggle on={w.enabled === 1} onChange={(on) => onRoom(w.webinar_id, on)} label={`On for ${w.title}`} />
            </div>
          ))
        ) : (
          <div className="small muted">Not running on a room yet.</div>
        )}
      </div>
    </section>
  );
}
